import { useState } from "react";
import { Search, ChevronRight, ChevronLeft, Coins, UserCog } from "lucide-react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { daysAgoISODate, todayISODate, MAX_RANGE_DAYS } from "@/stores/filterStore";
import { fmt } from "@/lib/format";
import MobileCreditAdjustSheet from "@/components/MobileCreditAdjustSheet";
import MobileRoleChangeSheet from "@/components/MobileRoleChangeSheet";

// 모바일 전용 사용자 관리 — 조회 중심(P2, 2026-09-17).
// desktop CreditsSection 의 큰 표를 모바일에 그대로 축소/가로스크롤하지 않고,
// 같은 API(analyticsApi.topUsers)만 재사용해 카드 리스트 + 인라인 상세로 재구성한다.
//
// 위험 운영 액션(크레딧 조정·일괄 지급·역할 변경)은 모바일에서 노출하지 않는다
// (§3 DESKTOP RECOMMENDED — 대량/파괴적 액션 오탭 방지). 상세는 조회 전용.
function defaultRange() {
  return { from: daysAgoISODate(MAX_RANGE_DAYS - 1), to: todayISODate() };
}

export default function MobileUserList() {
  const [range] = useState(defaultRange);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // 상세로 열린 user
  const [refreshTick, setRefreshTick] = useState(0);
  const [balanceOverride, setBalanceOverride] = useState({}); // userId -> 낙관적 잔액

  const { data, loading, error } = useAnalyticsQuery(
    (p, s) => analyticsApi.topUsers({ ...p, metric: "ncUsed", scope: "customer" }, s),
    [range.from, range.to, refreshTick],
    range
  );

  const users = (data?.users || []).map((u) =>
    u.userId in balanceOverride ? { ...u, balance: balanceOverride[u.userId] } : u
  );
  const q = search.trim().toLowerCase();
  const filtered = q
    ? users.filter((u) => (u.email || "").toLowerCase().includes(q) || (u.userId || "").toLowerCase().includes(q))
    : users;

  // 크레딧 조정 성공 — 낙관적 잔액 즉시 반영 + 서버 재조회.
  const onCreditSuccess = ({ userId, newBalance }) => {
    if (userId && newBalance != null) setBalanceOverride((prev) => ({ ...prev, [userId]: newBalance }));
    setRefreshTick((t) => t + 1);
  };

  if (selected) {
    const live = users.find((u) => u.userId === selected.userId) || selected;
    return <MobileUserDetail user={live} onBack={() => setSelected(null)} onCreditSuccess={onCreditSuccess} />;
  }

  return (
    <div className="space-y-3">
      {/* 검색 */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="이메일 / User ID 검색"
          className="h-11 w-full rounded-xl border border-ink-200 bg-white pl-9 pr-3 text-sm text-ink-800 placeholder:text-ink-400 focus:border-brand-400 focus:outline-none"
        />
      </div>

      {loading && !data && <div className="py-8 text-center text-sm text-ink-400">불러오는 중…</div>}
      {error && <div className="py-8 text-center text-sm text-red-600">불러오지 못했습니다 — {error.message}</div>}
      {!loading && filtered.length === 0 && (
        <div className="py-8 text-center text-sm text-ink-400">{q ? "검색 결과가 없습니다." : "사용자가 없습니다."}</div>
      )}

      <div className="space-y-2">
        {filtered.map((u) => (
          <button
            key={u.userId}
            type="button"
            onClick={() => setSelected(u)}
            className="flex w-full items-center gap-3 rounded-xl border border-ink-100 bg-white px-3.5 py-3 text-left"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-semibold text-ink-800">{u.email || u.userId}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-400">
                <span className="tabular-nums">잔액 {fmt.nc(u.balance ?? 0)}</span>
                {u.lastSeen && <span>· 최근 {fmt.kstDateTime(u.lastSeen)}</span>}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-ink-300" />
          </button>
        ))}
      </div>
    </div>
  );
}

// 모바일 사용자 상세 — 조회 + 운영 액션(크레딧 조정 · Role 변경). 위험 UX 는 sheet + 확인.
function MobileUserDetail({ user, onBack, onCreditSuccess }) {
  const u = user;
  const [sheet, setSheet] = useState(null); // "credit" | "role" | null
  const hasEmail = !!(u.email || "").trim();

  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-[13px] font-medium text-ink-500">
        <ChevronLeft className="h-4 w-4" /> 목록으로
      </button>

      <div className="rounded-xl border border-ink-100 bg-white p-4">
        <div className="truncate text-[15px] font-bold text-ink-900">{u.email || "(이메일 없음)"}</div>
        <div className="mt-0.5 break-all font-mono text-[11px] text-ink-400">{u.userId}</div>
      </div>

      {/* 크레딧 */}
      <Section title="크레딧">
        <Row label="현재 잔액" value={fmt.nc(u.balance ?? 0)} />
        {u.ncUsed != null && <Row label="사용 NC" value={fmt.nc(u.ncUsed)} />}
        {u.chargeCount != null && <Row label="충전 횟수" value={`${u.chargeCount}회`} />}
      </Section>

      {/* 사용 정보 */}
      <Section title="사용 정보">
        {u.lastSeen && <Row label="최근 활동" value={fmt.kstDateTime(u.lastSeen)} />}
        {(u.createdAtIso || u.createdAt) && (
          <Row label="가입" value={u.createdAtIso ? fmt.kstDateTime(u.createdAtIso) : u.createdAt} />
        )}
      </Section>

      {/* 운영 액션 — 최소 44px, sheet + 확인. Role 은 email 있는 계정만. */}
      <div className="space-y-2">
        <button type="button" onClick={() => setSheet("credit")}
          className="flex h-12 w-full items-center gap-2 rounded-xl border border-ink-200 bg-white px-4 text-[14px] font-semibold text-ink-800">
          <Coins className="h-4 w-4 text-brand-500" /> 크레딧 조정
        </button>
        {hasEmail && (
          <button type="button" onClick={() => setSheet("role")}
            className="flex h-12 w-full items-center gap-2 rounded-xl border border-ink-200 bg-white px-4 text-[14px] font-semibold text-ink-800">
            <UserCog className="h-4 w-4 text-brand-500" /> 계정 Role 변경
          </button>
        )}
      </div>

      {sheet === "credit" && (
        <MobileCreditAdjustSheet user={u} onClose={() => setSheet(null)} onSuccess={onCreditSuccess} />
      )}
      {sheet === "role" && (
        <MobileRoleChangeSheet user={u} onClose={() => setSheet(null)} />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-ink-100 bg-white">
      <div className="border-b border-ink-100 px-4 py-2 text-[12px] font-semibold text-ink-500">{title}</div>
      <div className="divide-y divide-ink-50">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-[12px] text-ink-500">{label}</span>
      <span className="text-[13px] font-medium tabular-nums text-ink-800">{value}</span>
    </div>
  );
}
